import React, { useEffect, useState } from 'react';
import './dataEditor.css';

// Helper functions
const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isArray = (v) => Array.isArray(v);

export const SECTION_TYPES = ['cardList', 'note', 'faqs', 'contentSection', 'serviceList'];
export const BG_COLORS = ['grey', 'light', 'white'];

export const normalizeSection = (obj) => {
  const v = isObject(obj) ? obj : {};
  const t = SECTION_TYPES.includes(String(v.type)) ? String(v.type) : 'cardList';
  return normalizeByType(v, t);
};

export const normalizeByType = (obj, type) => {
  const baseTitle = String((obj && obj.title) ?? '');
  const baseBg = BG_COLORS.includes(String(obj?.backgroundColor)) ? String(obj.backgroundColor) : 'grey';
  if (type === 'cardList') {
    const data = Array.isArray(obj?.data) ? obj.data : [];
    const normData = data.map((it) => ({ title: String(it?.title ?? ''), description: String(it?.description ?? '') }));
    return { type: 'cardList', title: baseTitle, backgroundColor: baseBg, data: normData };
  }
  if (type === 'note') {
    return { type: 'note', title: baseTitle, backgroundColor: baseBg, content: String(obj?.content ?? '') };
  }
  if (type === 'contentSection') {
    const subtitle = String(obj?.subtitle ?? '');
    const contentText = String(obj?.contentText ?? '');
    const contentList = Array.isArray(obj?.contentList) ? obj.contentList : [];
    const normContentList = contentList.map((item) => ({
      title: String(item?.title ?? ''),
      description: String(item?.description ?? '')
    }));
    const contentDisplaySide = ['left', 'right'].includes(String(obj?.contentDisplaySide)) 
      ? String(obj.contentDisplaySide) 
      : 'left';
    const backgroundImageUrl = String(obj?.backgroundImageUrl ?? '');
    return {
      type: 'contentSection',
      title: baseTitle,
      subtitle,
      contentText,
      contentList: normContentList,
      contentDisplaySide,
      backgroundImageUrl
    };
  }
  if (type === 'serviceList') {
    const services = Array.isArray(obj?.services) ? obj.services : [];
    const normServices = services.map((svc) => ({
      imageUrl: String(svc?.imageUrl ?? ''),
      title: String(svc?.title ?? ''),
      features: Array.isArray(svc?.features) ? svc.features.map(f => String(f ?? '')) : [],
      cost: typeof svc?.cost === 'number' && isFinite(svc.cost) ? svc.cost : undefined,
      frequency: ['annually', 'monthly', 'daily'].includes(String(svc?.frequency)) ? String(svc.frequency) : undefined,
      redirectURL: String(svc?.redirectURL ?? '')
    }));
    return { type: 'serviceList', title: baseTitle, backgroundColor: baseBg, services: normServices };
  }
  // faqs
  const questions = Array.isArray(obj?.questions) ? obj.questions : [];
  const normQs = questions.map((q) => ({ title: String(q?.title ?? ''), answer: String(q?.answer ?? '') }));
  return { type: 'faqs', title: baseTitle, questions: normQs };
};

const BackgroundColorSelect = ({ value, onChange }) => (
  <select className="form-select form-select-sm" value={BG_COLORS.includes(value) ? value : 'grey'} onChange={(e) => onChange(e.target.value)}>
    {BG_COLORS.map((c) => {
      const colorLabels = { 'grey': '灰色', 'light': '淺色', 'white': '白色' };
      return (<option key={c} value={c}>{colorLabels[c] || c}</option>);
    })}
  </select>
);

// Card list item editor component
const CardListItemEditor = ({ value, onChange }) => {
  const item = isObject(value) ? value : {};
  const updateField = (field, val) => onChange({ ...item, [field]: val });
  
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input 
            type="text" 
            className="form-control form-control-sm" 
            value={item.title ?? ''} 
            onChange={(e) => updateField('title', e.target.value)} 
          />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">描述</span>
        <div className="json-value flex-grow-1">
          <textarea 
            className="form-control form-control-sm" 
            rows={4} 
            value={item.description ?? ''} 
            onChange={(e) => updateField('description', e.target.value)} 
          />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">圖片URL (可選)</span>
        <div className="json-value flex-grow-1">
          <input 
            type="text" 
            className="form-control form-control-sm" 
            value={item.image ?? ''} 
            onChange={(e) => updateField('image', e.target.value)} 
            placeholder="圖片URL"
          />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">懸停內容 (可選)</span>
        <div className="json-value flex-grow-1">
          <textarea 
            className="form-control form-control-sm" 
            rows={3} 
            value={item.hoverContent ?? ''} 
            onChange={(e) => updateField('hoverContent', e.target.value)} 
            placeholder="懸停時顯示的額外內容"
          />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">二維碼圖片URL (可選)</span>
        <div className="json-value flex-grow-1">
          <input 
            type="text" 
            className="form-control form-control-sm" 
            value={item.qrCode ?? ''} 
            onChange={(e) => updateField('qrCode', e.target.value)} 
            placeholder="二維碼圖片URL"
          />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">二維碼文字 (可選)</span>
        <div className="json-value flex-grow-1">
          <input 
            type="text" 
            className="form-control form-control-sm" 
            value={item.qrCodeText ?? ''} 
            onChange={(e) => updateField('qrCodeText', e.target.value)} 
            placeholder="二維碼下方顯示的文字"
          />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">連結URL (可選)</span>
        <div className="json-value flex-grow-1">
          <input 
            type="text" 
            className="form-control form-control-sm" 
            value={item.linkTo ?? ''} 
            onChange={(e) => updateField('linkTo', e.target.value)} 
            placeholder="/path/to/page"
          />
        </div>
      </div>
    </div>
  );
};

const CardListSectionEditor = ({ value, onChange }) => {
  const v = normalizeByType(value, 'cardList');
  const patch = (p) => onChange({ ...v, ...p, type: 'cardList' });

  const items = Array.isArray(v.data) ? v.data : [];
  const updateData = (idx, next) => { 
    const nextArr = items.slice(); 
    nextArr[idx] = isObject(next) ? next : {}; 
    patch({ data: nextArr }); 
  };
  const addData = () => patch({ data: [...items, { title: '', description: '' }] });
  const removeData = (idx) => { const nextArr = items.slice(); nextArr.splice(idx, 1); patch({ data: nextArr }); };

  return (
    <div className="json-object editor-block border rounded p-2 bg-light w-100">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">背景顏色</span>
        <div className="json-value flex-grow-1">
          <BackgroundColorSelect value={v.backgroundColor} onChange={(nv) => patch({ backgroundColor: nv })} />
        </div>
      </div>
      <div className="json-field-row d-flex flex-column align-items-start gap-2 mb-2 w-100">
        <span className="json-key-label mb-1">數據</span>
        <div className="w-100">
          <div className="json-array editor-block border rounded p-2 bg-light w-100">
            {items.length === 0 && <div className="text-muted small">(無卡片)</div>}
            {items.map((it, idx) => (
              <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
                <div className="json-value flex-grow-1">
                  <CardListItemEditor 
                    value={it} 
                    onChange={(nv) => updateData(idx, nv)} 
                  />
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeData(idx)}>Delete</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addData}>+ 新增卡片</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NoteSectionEditor = ({ value, onChange }) => {
  const v = normalizeByType(value, 'note');
  const patch = (p) => onChange({ ...v, ...p, type: 'note' });
  return (
    <div className="json-object editor-block border rounded p-2 bg-light w-100">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">背景顏色</span>
        <div className="json-value flex-grow-1">
          <BackgroundColorSelect value={v.backgroundColor} onChange={(nv) => patch({ backgroundColor: nv })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">內容</span>
        <div className="json-value flex-grow-1">
          <textarea className="form-control form-control-sm" rows={6} value={v.content} onChange={(e) => patch({ content: e.target.value })} />
        </div>
      </div>
    </div>
  );
};

const FaqsSectionEditor = ({ value, onChange }) => {
  const v = normalizeByType(value, 'faqs');
  const patch = (p) => onChange({ ...v, ...p, type: 'faqs' });
  const items = Array.isArray(v.questions) ? v.questions : [];
  const updateQ = (idx, next) => { const nextArr = items.slice(); nextArr[idx] = { title: String(next?.title ?? ''), answer: String(next?.answer ?? '') }; patch({ questions: nextArr }); };
  const addQ = () => patch({ questions: [...items, { title: '', answer: '' }] });
  const removeQ = (idx) => { const nextArr = items.slice(); nextArr.splice(idx, 1); patch({ questions: nextArr }); };
  return (
    <div className="json-object editor-block border rounded p-2 bg-light w-100">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex flex-column align-items-start gap-2 mb-2 w-100">
        <span className="json-key-label mb-1">問題</span>
        <div className="w-100">
          <div className="json-array editor-block border rounded p-2 bg-light w-100">
            {items.length === 0 && <div className="text-muted small">(無問題)</div>}
            {items.map((it, idx) => (
              <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
                <div className="json-value flex-grow-1">
                  <div className="json-object editor-block border rounded p-2 bg-light">
                    <div className="json-field-row d-flex align-items-start gap-2 mb-2">
                      <span className="json-key-label">標題</span>
                      <div className="json-value flex-grow-1"><input type="text" className="form-control form-control-sm" value={it.title} onChange={(e) => updateQ(idx, { ...it, title: e.target.value })} /></div>
                    </div>
                    <div className="json-field-row d-flex align-items-start gap-2 mb-2">
                      <span className="json-key-label">答案</span>
                      <div className="json-value flex-grow-1"><textarea className="form-control form-control-sm" rows={4} value={it.answer} onChange={(e) => updateQ(idx, { ...it, answer: e.target.value })} /></div>
                    </div>
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeQ(idx)}>Delete</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addQ}>+ 新增問題</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContentSectionEditor = ({ value, onChange }) => {
  const v = normalizeByType(value, 'contentSection');
  const patch = (p) => onChange({ ...v, ...p, type: 'contentSection' });
  
  const contentList = Array.isArray(v.contentList) ? v.contentList : [];
  const updateContentListItem = (idx, next) => {
    const nextArr = contentList.slice();
    nextArr[idx] = isObject(next) ? next : {};
    patch({ contentList: nextArr });
  };
  const addContentListItem = () => patch({ contentList: [...contentList, { title: '', description: '' }] });
  const removeContentListItem = (idx) => {
    const nextArr = contentList.slice();
    nextArr.splice(idx, 1);
    patch({ contentList: nextArr });
  };
  
  return (
    <div className="json-object editor-block border rounded p-2 bg-light w-100">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">副標題 (可選)</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.subtitle} onChange={(e) => patch({ subtitle: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">內容文本 (可選)</span>
        <div className="json-value flex-grow-1">
          <textarea className="form-control form-control-sm" rows={6} value={v.contentText} onChange={(e) => patch({ contentText: e.target.value })} placeholder="輸入文本內容，支持換行" />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">內容顯示位置</span>
        <div className="json-value flex-grow-1">
          <select className="form-select form-select-sm" value={v.contentDisplaySide} onChange={(e) => patch({ contentDisplaySide: e.target.value })}>
            <option value="left">左側</option>
            <option value="right">右側</option>
          </select>
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">背景圖片URL (可選)</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.backgroundImageUrl} onChange={(e) => patch({ backgroundImageUrl: e.target.value })} placeholder="圖片URL" />
        </div>
      </div>
      <div className="json-field-row d-flex flex-column align-items-start gap-2 mb-2 w-100">
        <span className="json-key-label mb-1">內容列表 (可選)</span>
        <div className="w-100">
          <div className="json-array editor-block border rounded p-2 bg-light w-100">
            {contentList.length === 0 && <div className="text-muted small">(無列表項)</div>}
            {contentList.map((it, idx) => (
              <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
                <div className="json-value flex-grow-1">
                  <div className="json-object editor-block border rounded p-2 bg-light">
                    <div className="json-field-row d-flex align-items-start gap-2 mb-2">
                      <span className="json-key-label">標題</span>
                      <div className="json-value flex-grow-1">
                        <input type="text" className="form-control form-control-sm" value={it.title} onChange={(e) => updateContentListItem(idx, { ...it, title: e.target.value })} />
                      </div>
                    </div>
                    <div className="json-field-row d-flex align-items-start gap-2 mb-2">
                      <span className="json-key-label">描述</span>
                      <div className="json-value flex-grow-1">
                        <textarea className="form-control form-control-sm" rows={3} value={it.description} onChange={(e) => updateContentListItem(idx, { ...it, description: e.target.value })} />
                      </div>
                    </div>
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeContentListItem(idx)}>Delete</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addContentListItem}>+ 新增列表項</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceListItemEditor = ({ value, onChange }) => {
  const item = isObject(value) ? value : {};
  const updateField = (field, val) => onChange({ ...item, [field]: val });
  const features = Array.isArray(item.features) ? item.features : [];
  const updateFeature = (idx, val) => {
    const nextFeatures = features.slice();
    nextFeatures[idx] = String(val ?? '');
    onChange({ ...item, features: nextFeatures });
  };
  const addFeature = () => {
    onChange({ ...item, features: [...features, ''] });
  };
  const removeFeature = (idx) => {
    const nextFeatures = features.slice();
    nextFeatures.splice(idx, 1);
    onChange({ ...item, features: nextFeatures });
  };
  
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">圖片URL</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={item.imageUrl ?? ''} onChange={(e) => updateField('imageUrl', e.target.value)} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={item.title ?? ''} onChange={(e) => updateField('title', e.target.value)} />
        </div>
      </div>
      <div className="json-field-row d-flex flex-column align-items-start gap-2 mb-2 w-100">
        <span className="json-key-label mb-1">特性 (features)</span>
        <div className="w-100">
          <div className="json-array editor-block border rounded p-2 bg-light w-100">
            {features.length === 0 && <div className="text-muted small">(無特性)</div>}
            {features.map((feat, idx) => (
              <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
                <div className="json-value flex-grow-1">
                  <input type="text" className="form-control form-control-sm" value={feat} onChange={(e) => updateFeature(idx, e.target.value)} />
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeFeature(idx)}>Delete</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addFeature}>+ 新增特性</button>
          </div>
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">價格 (可選)</span>
        <div className="json-value flex-grow-1">
          <input type="number" step="0.01" className="form-control form-control-sm" value={item.cost ?? ''} onChange={(e) => { const n = parseFloat(e.target.value); updateField('cost', Number.isFinite(n) ? n : undefined); }} placeholder="例如: 16.09" />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">頻率 (可選)</span>
        <div className="json-value flex-grow-1">
          <select className="form-select form-select-sm" value={item.frequency ?? ''} onChange={(e) => updateField('frequency', e.target.value || undefined)}>
            <option value="">無</option>
            <option value="annually">年 (annually)</option>
            <option value="monthly">月 (monthly)</option>
            <option value="daily">日 (daily)</option>
          </select>
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">重定向URL (可選)</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={item.redirectURL ?? ''} onChange={(e) => updateField('redirectURL', e.target.value)} placeholder="/path/to/page" />
        </div>
      </div>
    </div>
  );
};

const ServiceListSectionEditor = ({ value, onChange }) => {
  const v = normalizeByType(value, 'serviceList');
  const patch = (p) => onChange({ ...v, ...p, type: 'serviceList' });

  const services = Array.isArray(v.services) ? v.services : [];
  const updateService = (idx, next) => {
    const nextArr = services.slice();
    nextArr[idx] = isObject(next) ? next : {};
    patch({ services: nextArr });
  };
  const addService = () => patch({ services: [...services, { imageUrl: '', title: '', features: [] }] });
  const removeService = (idx) => {
    const nextArr = services.slice();
    nextArr.splice(idx, 1);
    patch({ services: nextArr });
  };

  return (
    <div className="json-object editor-block border rounded p-2 bg-light w-100">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">標題</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={v.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">背景顏色</span>
        <div className="json-value flex-grow-1">
          <BackgroundColorSelect value={v.backgroundColor} onChange={(nv) => patch({ backgroundColor: nv })} />
        </div>
      </div>
      <div className="json-field-row d-flex flex-column align-items-start gap-2 mb-2 w-100">
        <span className="json-key-label mb-1">服務列表</span>
        <div className="w-100">
          <div className="json-array editor-block border rounded p-2 bg-light w-100">
            {services.length === 0 && <div className="text-muted small">(無服務)</div>}
            {services.map((svc, idx) => (
              <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
                <div className="json-value flex-grow-1">
                  <ServiceListItemEditor value={svc} onChange={(nv) => updateService(idx, nv)} />
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeService(idx)}>Delete</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addService}>+ 新增服務</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SectionItemEditor = ({ value, onChange }) => {
  const v = normalizeSection(value);
  const handleTypeChange = (newType) => {
    const next = normalizeByType(v, newType);
    onChange(next);
  };
  return (
    <div className="w-100">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">類型</span>
        <div className="json-value flex-grow-1" style={{ maxWidth: 220 }}>
          <select className="form-select form-select-sm" value={v.type} onChange={(e) => handleTypeChange(e.target.value)}>
            {SECTION_TYPES.map((t) => {
              const typeLabels = { 'cardList': '卡片列表', 'note': '備註', 'faqs': '常見問題', 'contentSection': '內容區塊', 'serviceList': '服務列表' };
              return (<option key={t} value={t}>{typeLabels[t] || t}</option>);
            })}
          </select>
        </div>
      </div>
      {v.type === 'cardList' && (<CardListSectionEditor value={v} onChange={onChange} />)}
      {v.type === 'note' && (<NoteSectionEditor value={v} onChange={onChange} />)}
      {v.type === 'faqs' && (<FaqsSectionEditor value={v} onChange={onChange} />)}
      {v.type === 'contentSection' && (<ContentSectionEditor value={v} onChange={onChange} />)}
      {v.type === 'serviceList' && (<ServiceListSectionEditor value={v} onChange={onChange} />)}
    </div>
  );
};

const SectionsEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (idx, nextVal) => { const next = items.slice(); next[idx] = normalizeSection(nextVal); onChange(next); };
  const removeItem = (idx) => { const next = items.slice(); next.splice(idx, 1); onChange(next); };
  const addItem = () => onChange([...(items || []), normalizeByType({}, 'cardList')]);
  
  // Track which sections are expanded (default all collapsed)
  const [expandedSections, setExpandedSections] = useState({});
  
  // Update expanded state when items change
  useEffect(() => {
    setExpandedSections(prev => {
      const newExpanded = {};
      items.forEach((_, idx) => {
        // Keep existing expanded state if item still exists, otherwise default to false (collapsed)
        if (prev[idx] !== undefined) {
          newExpanded[idx] = prev[idx];
        } else {
          newExpanded[idx] = false; // Default to collapsed for new items
        }
      });
      return newExpanded;
    });
  }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const toggleSection = (idx) => {
    setExpandedSections(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  
  // Get section title for display
  const getSectionTitle = (section) => {
    if (section.title) return section.title;
    const typeLabels = { 'cardList': '卡片列表', 'note': '備註', 'faqs': '常見問題', 'contentSection': '內容區塊', 'serviceList': '服務列表' };
    return typeLabels[section.type] || section.type || '區塊';
  };
  
  return (
    <div className="json-array editor-block border rounded p-2 bg-light w-100">
      {items.length === 0 && <div className="text-muted small">(無區塊)</div>}
      {items.map((it, idx) => (
        <div key={idx} className="mb-3 border rounded p-2 bg-white">
          {/* Section Header with Collapse Toggle */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <button
              type="button"
              className="btn btn-sm btn-link text-start p-0 text-decoration-none flex-grow-1"
              onClick={() => toggleSection(idx)}
              style={{ fontWeight: '500' }}
            >
              <i className={`fas fa-chevron-${expandedSections[idx] ? 'down' : 'right'} me-2`}></i>
              {getSectionTitle(it)} {idx + 1}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {expandedSections[idx] && (
            <div className="mt-2">
              <SectionItemEditor value={it} onChange={(nv) => updateItem(idx, nv)} />
            </div>
          )}
          
          {/* Delete Button at Bottom */}
          <div className="d-flex justify-content-end mt-2 pt-2 border-top">
            <button 
              type="button" 
              className="btn btn-sm btn-outline-danger" 
              onClick={() => removeItem(idx)}
            >
              刪除
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ 新增區塊</button>
    </div>
  );
};

export default SectionsEditor;

